import { BinanceUnwrapQueueAbi } from '../abis/binance'
import type { LstWithdrawalRequest } from '../types'
import {
  computeStatus,
  toBigInt,
  toNumber,
  type LstWithdrawalReader,
} from './shared'

/**
 * Binance wBETH — `UnwrapTokenV1` queue, fully enumerable on-chain.
 *
 * One multicall: `getUserWithdrawRequests(user)` + `lockTime()`. The
 * getter returns the user's *open* requests as structs (claimed ones are
 * popped), so no pagination and no caller-supplied ids are needed.
 *
 * THREE things here are easy to get wrong:
 *
 * 1. **`requestId` is the user's ARRAY POSITION, not a stable id.**
 *    `claimWithdraw(_index)` indexes `userWithdrawRequests[user]`, and that
 *    array is maintained with swap-and-pop — claiming slot 0 moves the last
 *    element into slot 0. So any cached id is wrong after a single claim.
 *    Consumers must re-read this list immediately before building a claim.
 *    The global queue index is deliberately NOT surfaced as `requestId`:
 *    the claim call cannot accept it.
 *
 * 2. **The payout is frozen at request time.** `ethAmount` was computed
 *    from the exchange rate when the request was made and is paid out
 *    verbatim, so the position stops earning for the whole lock. That is
 *    why `amountUnderlying` is the recorded ETH, not a live conversion of
 *    `wbethAmount`.
 *
 * 3. **Time alone does not make a request claimable.** `claimWithdraw`
 *    also requires `allocated`, which is only set when Binance's operator
 *    budget (`availableAllocateAmount`) covered the request — small ones
 *    are allocated inline at request time, larger ones wait for an
 *    `allocate()` call with no SLA. An unallocated request is reported
 *    `pending` however old it is.
 *
 * Not modelled: `claimWithdraw` additionally requires the queue contract's
 * own ETH balance to cover the request. That is a global condition we
 * cannot attribute per-user, so a request can read `claimable` here and
 * still revert `Not enough balance` until Binance recharges.
 */
export const readerBinanceWbeth: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    if (!entry.withdrawalContract) return []

    const res = (await multicallRetry({
      chain: chainId,
      calls: [
        {
          address: entry.withdrawalContract,
          name: 'getUserWithdrawRequests',
          params: [user],
        },
        {
          address: entry.withdrawalContract,
          name: 'lockTime',
          params: [],
        },
      ] as unknown as never[],
      abi: [BinanceUnwrapQueueAbi, BinanceUnwrapQueueAbi] as unknown as never,
    })) as unknown[]

    const reqs = res[0] as Array<Record<string, unknown>> | undefined
    const lockTime = toNumber(res[1])
    if (!Array.isArray(reqs) || lockTime === undefined) return []

    const out: LstWithdrawalRequest[] = []
    for (let i = 0; i < reqs.length; i++) {
      const r = reqs[i]
      const triggerTime = toNumber(r.triggerTime)
      const ethAmount = toBigInt(r.ethAmount)
      const wbethAmount = toBigInt(r.wbethAmount)
      if (triggerTime === undefined || ethAmount === undefined) continue

      const readyAt = triggerTime + lockTime
      const claimed = (toNumber(r.claimTime) ?? 0) > 0
      const allocated = r.allocated === true

      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        // Positional — see note 1 above.
        requestId: String(i),
        amountUnderlying: ethAmount.toString(),
        shares: wbethAmount?.toString(),
        // Claimed requests are popped from the array, so this branch is
        // defensive only.
        status: claimed
          ? ('claimed' as const)
          : allocated
            ? computeStatus(readyAt)
            : ('pending' as const),
        readyAt,
      })
    }
    return out
  },
}
